const MIN_SCALE = 1;
const MAX_SCALE = 4;
/** Movement below this (px²) keeps a pointer-gesture a tap — pin
 *  clicks must survive the pan handler. Same magnitude as the 3D
 *  tap-vs-orbit threshold. */
const PAN_ENGAGE_PX2 = 36;
export class IllustrationRenderer {
    canRender(item) {
        return item.kind === 'illustration' || item.kind === 'field_photo';
    }
    mount(container, item, opts) {
        let activeId = opts.activeAnnotationId;
        let hoveredId = opts.hoveredAnnotationId;
        let currentTheme = opts.theme;
        let destroyed = false;
        const root = document.createElement('div');
        root.style.cssText =
            'position:relative;width:100%;height:100%;overflow:hidden;background:#000;touch-action:none;';
        container.appendChild(root);
        // Single transformed layer — image + pins move/scale together.
        const zoomLayer = document.createElement('div');
        zoomLayer.style.cssText =
            'position:absolute;inset:0;transform-origin:0 0;will-change:transform;';
        root.appendChild(zoomLayer);
        const addOrient = (u) => u.includes('?') ? `${u}&orient=auto` : `${u}?orient=auto`;
        const img = document.createElement('img');
        img.src = typeof item.url === 'string' ? addOrient(item.url) : '';
        if (typeof item.url === 'function') {
            void item.url().then((u) => {
                if (destroyed)
                    return;
                img.src = addOrient(u);
            });
        }
        img.alt = '';
        img.draggable = false;
        img.style.cssText =
            'width:100%;height:100%;object-fit:contain;display:block;-webkit-user-drag:none;user-select:none;';
        img.onload = () => {
            if (destroyed)
                return;
            opts.onReady?.({ is3D: false, hasAudio: false });
        };
        img.onerror = () => {
            if (destroyed)
                return;
            opts.onError?.(new Error('Illustration failed to load'));
        };
        zoomLayer.appendChild(img);
        const pinsLayer = document.createElement('div');
        pinsLayer.style.cssText = 'position:absolute;inset:0;pointer-events:none;';
        pinsLayer.setAttribute('role', 'group');
        pinsLayer.setAttribute('aria-label', 'Schaubild-Annotationen');
        zoomLayer.appendChild(pinsLayer);
        function renderPins() {
            pinsLayer.innerHTML = '';
            const anns = item.annotations ?? [];
            anns.forEach((ann, i) => {
                if (!ann.anchor)
                    return;
                const active = ann.id === activeId;
                const hovered = ann.id === hoveredId;
                const pin = document.createElement('button');
                pin.type = 'button';
                pin.textContent = String(i + 1);
                pin.setAttribute('aria-label', `Annotation ${i + 1}: ${ann.label}`);
                pin.setAttribute('aria-pressed', active ? 'true' : 'false');
                const sz = active ? (currentTheme.pinSize.active ?? 36) : (currentTheme.pinSize.idle ?? 28);
                pin.style.cssText = [
                    'position:absolute',
                    `left:${ann.anchor.x * 100}%`,
                    `top:${ann.anchor.y * 100}%`,
                    'transform:translate(-50%,-50%)',
                    `width:${sz}px`,
                    `height:${sz}px`,
                    'border-radius:50%',
                    `background:${active ? currentTheme.pinColor.active : (hovered ? currentTheme.pinColor.hovered : currentTheme.pinColor.idle)}`,
                    `color:${active ? currentTheme.pinTextColor.active : currentTheme.pinTextColor.idle}`,
                    `border:2px solid ${active ? (currentTheme.pinBorderColor?.active ?? '#fff') : (currentTheme.pinBorderColor?.idle ?? 'rgba(0,0,0,0.25)')}`,
                    `box-shadow:${currentTheme.pinShadow ?? '0 2px 8px rgba(0,0,0,0.4)'}`,
                    'font-weight:bold',
                    `font-family:${currentTheme.pinFont ?? 'system-ui,sans-serif'}`,
                    'cursor:pointer',
                    'pointer-events:auto',
                    `transition:all ${currentTheme.pinTransitionMs ?? 200}ms ease`,
                    'display:flex',
                    'align-items:center',
                    'justify-content:center',
                    'padding:0',
                ].join(';');
                pin.dataset.annId = ann.id;
                pin.addEventListener('click', () => opts.onSelectAnnotation(ann.id));
                pin.addEventListener('pointerenter', () => opts.onHoverAnnotation(ann.id));
                pin.addEventListener('pointerleave', () => opts.onHoverAnnotation(null));
                pinsLayer.appendChild(pin);
            });
            emitMascot();
        }
        // Mascot position for the active pin — same contract as the 3D
        // renderer (safe-clamped pin-relative, bottom-center fallback,
        // once-per-state-change on fallback). Reads the rendered button's
        // rect so zoom/pan transforms are already baked in. NO pinHelpers
        // import here — that would drag three.js back into the 2D bundle.
        let lastWasBottomCenter = false;
        function emitMascot() {
            if (!opts.onMascotPositionChange || destroyed)
                return;
            if (!activeId)
                return;
            const pin = pinsLayer.querySelector(`[data-ann-id="${CSS.escape(activeId)}"]`);
            const rootRect = root.getBoundingClientRect();
            if (!pin || rootRect.width === 0)
                return;
            const pinRect = pin.getBoundingClientRect();
            const x = pinRect.left + pinRect.width / 2 - rootRect.left;
            const y = pinRect.top + pinRect.height / 2 - rootRect.top;
            const margin = 80;
            const safeX = Math.max(margin, Math.min(rootRect.width - margin, x));
            const safeY = Math.max(margin, Math.min(rootRect.height - margin, y));
            const offscreen = x < 0 || y < 0 || x > rootRect.width || y > rootRect.height;
            if (offscreen || Math.abs(safeX - x) > 40 || Math.abs(safeY - y) > 40) {
                if (!lastWasBottomCenter) {
                    lastWasBottomCenter = true;
                    opts.onMascotPositionChange({ strategy: 'bottom-center' });
                }
                return;
            }
            lastWasBottomCenter = false;
            opts.onMascotPositionChange({ strategy: 'pin-relative', x: safeX, y: safeY });
        }
        renderPins();
        // ── Pinch-zoom + pan ──
        // transform = translate(tx,ty) scale(s), origin 0 0. A container
        // point p maps to content coord (p - t)/s; zooming around p keeps
        // that coord fixed: t' = p - (p - t) * s'/s.
        let scale = 1;
        let tx = 0;
        let ty = 0;
        const pointers = new Map();
        let lastPinchDist = null;
        let lastMid = null;
        let lastPan = null;
        let panEngaged = false;
        const applyTransform = () => {
            zoomLayer.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
            emitMascot();
        };
        const clampTransform = () => {
            if (scale <= MIN_SCALE + 0.01) {
                scale = MIN_SCALE;
                tx = 0;
                ty = 0;
                return;
            }
            const rect = root.getBoundingClientRect();
            // Visible content spans [t, t + size*s] — keep the container covered.
            tx = Math.min(0, Math.max(rect.width * (1 - scale), tx));
            ty = Math.min(0, Math.max(rect.height * (1 - scale), ty));
        };
        const zoomAround = (cx, cy, nextScaleRaw) => {
            const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScaleRaw));
            tx = cx - ((cx - tx) * nextScale) / scale;
            ty = cy - ((cy - ty) * nextScale) / scale;
            scale = nextScale;
            clampTransform();
            applyTransform();
        };
        const onPointerDown = (e) => {
            pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
            if (pointers.size === 2) {
                const [a, b] = [...pointers.values()];
                lastPinchDist = Math.hypot(a.x - b.x, a.y - b.y);
                lastMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
                lastPan = null;
            }
            else if (pointers.size === 1) {
                lastPan = { x: e.clientX, y: e.clientY };
                panEngaged = false;
            }
        };
        const onPointerMove = (e) => {
            if (!pointers.has(e.pointerId))
                return;
            pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
            const rect = root.getBoundingClientRect();
            if (pointers.size === 2 && lastPinchDist !== null && lastMid !== null) {
                const [a, b] = [...pointers.values()];
                const dist = Math.hypot(a.x - b.x, a.y - b.y);
                const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
                if (lastPinchDist > 0) {
                    zoomAround(mid.x - rect.left, mid.y - rect.top, scale * (dist / lastPinchDist));
                }
                // Two-finger pan rides along with the midpoint.
                tx += mid.x - lastMid.x;
                ty += mid.y - lastMid.y;
                clampTransform();
                applyTransform();
                lastPinchDist = dist;
                lastMid = mid;
            }
            else if (pointers.size === 1 && scale > 1 && lastPan) {
                const dx = e.clientX - lastPan.x;
                const dy = e.clientY - lastPan.y;
                // Stay a tap until real movement — pin clicks must fire.
                if (!panEngaged && dx * dx + dy * dy < PAN_ENGAGE_PX2)
                    return;
                panEngaged = true;
                tx += dx;
                ty += dy;
                lastPan = { x: e.clientX, y: e.clientY };
                clampTransform();
                applyTransform();
            }
        };
        const onPointerEnd = (e) => {
            pointers.delete(e.pointerId);
            if (pointers.size < 2) {
                lastPinchDist = null;
                lastMid = null;
            }
            if (pointers.size === 1) {
                const [p] = [...pointers.values()];
                lastPan = { x: p.x, y: p.y };
                panEngaged = false;
            }
            else if (pointers.size === 0) {
                lastPan = null;
            }
        };
        // Zoom per Mausrad (Desktop) UND Trackpad-Pinch (kommt als ctrl+wheel).
        // Beide zoomen um den Cursor; preventDefault stoppt Seiten-Scroll überm
        // Bild. (Vorher nur ctrl+wheel → mit echter Maus ging Zoom gar nicht.)
        const onWheel = (e) => {
            e.preventDefault();
            const rect = root.getBoundingClientRect();
            zoomAround(e.clientX - rect.left, e.clientY - rect.top, scale * (e.deltaY < 0 ? 1.1 : 0.9));
        };
        root.addEventListener('pointerdown', onPointerDown);
        root.addEventListener('pointermove', onPointerMove);
        root.addEventListener('pointerup', onPointerEnd);
        root.addEventListener('pointercancel', onPointerEnd);
        root.addEventListener('pointerleave', onPointerEnd);
        root.addEventListener('wheel', onWheel, { passive: false });
        const unsubActive = opts.signal?.onActiveAnnotationChange?.((id) => {
            activeId = id;
            renderPins();
        });
        const unsubHover = opts.signal?.onHoverAnnotationChange?.((id) => {
            hoveredId = id;
            renderPins();
        });
        const unsubTheme = opts.signal?.onThemeChange?.((t) => {
            currentTheme = t;
            renderPins();
        });
        return {
            update(partial) {
                if (partial.activeAnnotationId !== undefined) {
                    activeId = partial.activeAnnotationId;
                    renderPins();
                }
                if (partial.hoveredAnnotationId !== undefined) {
                    hoveredId = partial.hoveredAnnotationId;
                    renderPins();
                }
                if (partial.theme) {
                    currentTheme = partial.theme;
                    renderPins();
                }
            },
            destroy() {
                destroyed = true;
                root.removeEventListener('pointerdown', onPointerDown);
                root.removeEventListener('pointermove', onPointerMove);
                root.removeEventListener('pointerup', onPointerEnd);
                root.removeEventListener('pointercancel', onPointerEnd);
                root.removeEventListener('pointerleave', onPointerEnd);
                root.removeEventListener('wheel', onWheel);
                unsubActive?.();
                unsubHover?.();
                unsubTheme?.();
                root.remove();
            },
        };
    }
}
//# sourceMappingURL=IllustrationRenderer.js.map